import { tt } from './i18n/translation-memory'

/**
 * The period a Control Tower report is asked about - `SAAS.md §3.84`.
 *
 * **Why this exists at all.** Four Tower screens - `/profitability`, `/franchises/:id/usage`,
 * `/cardpay` and `/billing` - render a fixed window and cannot be asked a question, while the
 * endpoints behind them have declared optional `from` and `to` the whole time. Legacy gives each of
 * those screens a date range and a row of presets. The owner's words were *"it still feels really
 * incomplete"*, and a report that adds up correctly and answers one question nobody asked is exactly
 * that.
 *
 * **These are ISO instants, not calendar dates, and the distinction is load-bearing.** The server
 * parses a bare `2026-09-23` as midnight **UTC** (`Instants.parseOrNull`, third branch), which in
 * Europe/Bratislava is 02:00 - so `to=<today>` would exclude almost the whole of today and
 * `from=<today>` would silently drop its first two hours. Legacy computes the boundaries in **local**
 * time and sends `toISOString()`, which lands on the real local midnight. That is kept, and it is the
 * frame every Tower report window is in - `monthWindow` included, since a second spelling of it put two
 * screens on one endpoint 4 calls apart (`SAAS.md §3.188`).
 *
 * **AND LEGACY'S OWN STATED REASON FOR IT IS FALSE OF THIS BACKEND, which is why it is no longer quoted
 * as one.** Its comment reads *"the backend buckets days in Europe/Bratislava too, so Včera lines up
 * with the calendar day"*; `PlatformUsageService.franchiseUsage` buckets its daily table
 * `AT TIME ZONE 'UTC'`, **by name**, and its own comment says the UTC spelling is deliberate because a
 * JDBC session would otherwise take the JVM zone. So the window edges are local and the bucket is UTC,
 * and a one-day window draws **two** bars: a sliver under the previous day's date and the rest of the
 * day. Measured on the owner's rows: **59 of 112 126** `api_usage_log` rows fall on different calendar
 * days in the two frames, 57 `google_maps` and 2 `resend`, all at 0 cents.
 *
 * **The bucket is not moved, and that is parity rather than effort**: Europe/Bratislava would line the
 * bars up with the edges and also move every figure legacy reports, on a cost report, for 59 rows worth
 * nothing. The local edges stay because they are what the operator's day is; the mismatch is recorded
 * here so the next reader meets it rather than re-deriving it.
 *
 * **Not `toDateParam` and not the manager's `periods.ts`.** Those produce a bare local date, which is
 * right for the manager's endpoints because those take a business day. Mixing the two is how a report
 * loses two hours at each end, so the two helpers stay separate and this comment says which is
 * which. `§5` also forbids the Tower importing a module's copy.
 */
export type ReportPreset =
  | 'today'
  | 'yesterday'
  | '7d'
  | '30d'
  | '90d'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisYear'
  | 'lastYear'

export interface ReportWindow {
  /** ISO instant, or `''` for *whatever the server already defaults to*. */
  from: string
  to: string
}

/** No window at all: the screen sends neither parameter and the server picks, exactly as today. */
export const WHOLE_DEFAULT_WINDOW: ReportWindow = { from: '', to: '' }

/**
 * Each screen's own preset row, taken from that legacy page rather than unified.
 *
 * **Deliberately three different lists.** The operator of `/cardpay` wants *yesterday*; the operator
 * of `/profitability` wants *year to date*; nobody looking at a franchise's API usage wants
 * *yesterday*. Legacy's three lists differ for that reason and inventing one superset row would put
 * five useless buttons on each screen.
 */
export const PROFITABILITY_PRESETS: readonly ReportPreset[] = [
  '7d',
  '30d',
  '90d',
  'thisMonth',
  'thisYear',
]
export const USAGE_PRESETS: readonly ReportPreset[] = [
  '7d',
  '30d',
  '90d',
  'thisMonth',
  'lastMonth',
  'thisYear',
  'lastYear',
]
export const CARDPAY_PRESETS: readonly ReportPreset[] = [
  'yesterday',
  'today',
  '7d',
  '30d',
  'thisMonth',
  'lastMonth',
]
/*
 * There is no `BILLING_PRESETS` and there should not be - `SAAS.md §3.104`.
 *
 * One was declared here, exported, and **read by nothing**, under the javadoc above that says
 * *"deliberately three different lists"*. It could not be read either: `BillingPage`'s own javadoc
 * records why - *"Client-side, because the endpoint takes no window: `/super-admin/billing/invoices`
 * declares no `from`/`to`, unlike the three other Tower reports, so the rows arrive whole and the
 * screen narrows them - which is what legacy does too."*
 *
 * So §3.84 did reach all four report screens; three through `from`/`to` and `/billing` by filtering
 * what it already has. The list was the only part left implying a bar that screen cannot render.
 */

export function reportPresetLabel(preset: ReportPreset): string {
  if (preset === 'today') return tt('Dnes')
  if (preset === 'yesterday') return tt('Včera')
  if (preset === '7d') return tt('7 dní')
  if (preset === '30d') return tt('30 dní')
  if (preset === '90d') return tt('90 dní')
  if (preset === 'thisMonth') return tt('Tento mesiac')
  if (preset === 'lastMonth') return tt('Minulý mesiac')
  if (preset === 'thisYear') return tt('Tento rok')
  return tt('Minulý rok')
}

/** Local midnight of the day `date` falls in. */
function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function shiftDays(date: Date, days: number): Date {
  const copy = new Date(date)
  copy.setDate(copy.getDate() + days)
  return copy
}

/** The last instant before `date`, so a closed period does not overlap the next one by a millisecond. */
function justBefore(date: Date): Date {
  return new Date(date.getTime() - 1)
}

/**
 * What a preset means, as two ISO instants.
 *
 * `now` is injectable because the two moments this can get wrong are half past midnight and the 1st
 * of a month, and waiting for either is not a test.
 *
 * **`7d` is seven calendar days including today, not a rolling 168 hours** - and legacy disagrees
 * with itself here, which is why this is written down. `FranchiseUsage` uses `daysAgoIso(7)`, a
 * rolling window from this time of day seven days ago; `CardPay` uses `startOfDay - 6 days`, seven
 * whole days. The calendar reading is kept for both: the server buckets its rows by day, so a window
 * starting at 14:23 puts a partial day at the edge of a report whose rows are whole days, and a
 * button labelled *7 dní* that answers differently at breakfast and at bedtime is the kind of thing
 * that gets reported as a bug in the figures. `30d` and `90d` follow it.
 *
 * **`this_month` also differs between them and here it does not.** Legacy's `FranchiseUsage` builds
 * it with `Date.UTC(...)` while `CardPay` builds it locally; the local one is correct on legacy's own
 * argument, quoted at the top of this file, so it is the one kept.
 */
export function reportPresetRange(preset: ReportPreset, now: Date = new Date()): ReportWindow {
  const today = startOfDay(now)
  const iso = (date: Date): string => date.toISOString()
  const untilNow = iso(now)

  if (preset === 'today') return { from: iso(today), to: untilNow }
  if (preset === 'yesterday') {
    return { from: iso(shiftDays(today, -1)), to: iso(justBefore(today)) }
  }
  if (preset === '7d') return { from: iso(shiftDays(today, -6)), to: untilNow }
  if (preset === '30d') return { from: iso(shiftDays(today, -29)), to: untilNow }
  if (preset === '90d') return { from: iso(shiftDays(today, -89)), to: untilNow }
  if (preset === 'thisMonth') {
    return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: untilNow }
  }
  if (preset === 'lastMonth') {
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    return { from: iso(first), to: iso(justBefore(new Date(now.getFullYear(), now.getMonth(), 1))) }
  }
  if (preset === 'thisYear') {
    return { from: iso(new Date(now.getFullYear(), 0, 1)), to: untilNow }
  }
  const firstOfLastYear = new Date(now.getFullYear() - 1, 0, 1)
  return { from: iso(firstOfLastYear), to: iso(justBefore(new Date(now.getFullYear(), 0, 1))) }
}

/**
 * The window as the query parameters to send, dropping an empty one entirely.
 *
 * **An absent parameter is not the same as an empty one.** The server's default window is chosen
 * when `from` is `null`; a `from=` would reach `Instants.parseOrNull`, come back null from its first
 * branch on a blank string, and the caller would get whatever that endpoint does with an unparseable
 * value. So a screen with no window selected must send nothing, which is also what makes this change
 * additive: opened fresh, every one of these reports shows exactly what it shows today.
 */
export function reportWindowParams(window: ReportWindow): { from?: string; to?: string } {
  return {
    ...(window.from === '' ? {} : { from: window.from }),
    ...(window.to === '' ? {} : { to: window.to }),
  }
}

/** `2026-09-23T00:00:00.000Z` -> `2026-09-23`, for a `<input type="date">`. */
export function dateInputValue(isoInstant: string): string {
  if (isoInstant === '') return ''
  const parsed = new Date(isoInstant)
  if (Number.isNaN(parsed.getTime())) return ''
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const day = String(parsed.getDate()).padStart(2, '0')
  return `${parsed.getFullYear()}-${month}-${day}`
}

/**
 * A date the operator typed, as the instant that bound means.
 *
 * `from` takes local midnight of that day and `to` takes the last millisecond of it, so picking the
 * same day twice asks about that whole day rather than about an empty instant - which is what a
 * naive `new Date(value)` on both ends produces, and it reads as *the report is broken today*.
 */
export function dateInputToInstant(value: string, edge: 'from' | 'to'): string {
  if (value === '') return ''
  const [year, month, day] = value.split('-').map(Number)
  if (year === undefined || month === undefined || day === undefined) return ''
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return ''
  const midnight = new Date(year, month - 1, day)
  if (Number.isNaN(midnight.getTime())) return ''
  return edge === 'from'
    ? midnight.toISOString()
    : justBefore(shiftDays(midnight, 1)).toISOString()
}

/**
 * A whole calendar month, as `<input type="month">` gives it - `SAAS.md §3.188`, FB-964.
 *
 * **Here because a second spelling of this put two Tower screens in two time frames.**
 * `ApiPricingPage` built its own with `Date.UTC(...)`, so `GET /franchises/{id}/usage` answered
 * **101** calls for franchise 1 in September where `FranchiseUsagePage` read **97** - the four being
 * `google_maps` calls made after 22:00 UTC on 30 September, which is 1 October in Bratislava. Same
 * endpoint, same month, two figures. The frame belongs in one place and this is it.
 *
 * **Not `reportPresetRange('thisMonth')`, which is a different question.** That preset ends at *now*,
 * because a report about the month in progress must not claim days that have not happened; a pricing
 * month is a **closed period** whose `to` is its own last millisecond whether or not the month is
 * over. Asking either one for the other's answer is wrong in a way no figure on the screen shows.
 *
 * **The last millisecond rather than `23:59:59`**: a second-precision end drops the final 999 ms, so a
 * call logged there falls into neither month's window - a row on no screen at all, which is worse than
 * a row on the wrong one.
 *
 * A month this cannot read answers {@link WHOLE_DEFAULT_WINDOW}, so the screen sends no bound and the
 * server picks - never a window computed from `NaN`, which serialises as an exception.
 */
export function monthWindow(month: string): ReportWindow {
  const [year, index] = month.split('-').map(Number)
  if (year === undefined || index === undefined) return WHOLE_DEFAULT_WINDOW
  if (!Number.isFinite(year) || !Number.isFinite(index)) return WHOLE_DEFAULT_WINDOW
  if (index < 1 || index > 12) return WHOLE_DEFAULT_WINDOW
  const first = new Date(year, index - 1, 1)
  if (Number.isNaN(first.getTime())) return WHOLE_DEFAULT_WINDOW
  return { from: first.toISOString(), to: justBefore(new Date(year, index, 1)).toISOString() }
}

/** The month `now` falls in, in the viewer's own zone, as `<input type="month">` wants it. */
export function currentMonthInput(now: Date = new Date()): string {
  /* `getUTCMonth()` is the reflex and it is wrong here for §3.153's reason: between midnight and
   * 02:00 in Bratislava it answers the **previous** month, so the screen opens on a month the
   * operator did not ask for on the one day of the month they are most likely to be looking. */
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

/** Which preset the current window is, if any - so the pressed button can look pressed. */
export function activePreset(
  window: ReportWindow,
  presets: readonly ReportPreset[],
  now: Date = new Date(),
): ReportPreset | null {
  if (window.from === '') return null
  for (const preset of presets) {
    const range = reportPresetRange(preset, now)
    // `to` is compared by its day rather than its instant: every open-ended preset ends at *now*,
    // which has moved by the time the operator looks at the row.
    if (range.from === window.from && dateInputValue(range.to) === dateInputValue(window.to)) {
      return preset
    }
  }
  return null
}
